import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { identityApi, type LoginRequest, type Me } from '@/api/identity';
import { configureHttp, refreshSession } from '@/lib/http';
import { session } from '@/lib/session';
import { setListFilterOwner } from '@/lib/listFilters';
import { qk } from '@/lib/queryKeys';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Feedback';
import { AuthContext, useSessionStatus } from './useAuth';

/**
 * Owns the session lifecycle:
 *  - on load with a stored refresh token → refresh once to get an access token + user;
 *  - wires lib/http callbacks (refreshed session → cache `me`; refresh rejected → clear caches, guards redirect to /login).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const status = useSessionStatus();
  const [bootError, setBootError] = useState(false);

  useEffect(() => {
    configureHttp({
      onUnauthenticated: () => qc.clear(),
      onSessionRefreshed: (payload) => {
        const user = payload.user as Me;
        setListFilterOwner({ tenantId: user.tenantId, userId: user.id });
        qc.setQueryData(qk.me, user);
      },
    });
  }, [qc]);

  const boot = useCallback(() => {
    setBootError(false);
    void refreshSession().then((ok) => {
      // false + still "unknown" = network failure (refresh token kept): offer retry instead of logging out
      if (!ok && session.getStatus() === 'unknown') setBootError(true);
    });
  }, []);

  useEffect(() => {
    if (status === 'unknown') boot();
  }, [status, boot]);

  const login = useCallback(
    async (req: LoginRequest) => {
      const res = await identityApi.login(req);
      qc.clear();
      session.setTokens(res.accessToken, res.accessTokenExpiresAtUtc, res.refreshToken);
      // A different user/tenant than the previous session in this browser → drop that user's saved list filters.
      setListFilterOwner({ tenantId: res.user.tenantId, userId: res.user.id });
      qc.setQueryData(qk.me, res.user);
      return res.user;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    const rt = session.getRefreshToken();
    try {
      if (rt) await identityApi.logout(rt);
    } catch {
      /* server unreachable — still clear locally */
    }
    session.clear();
    qc.clear();
  }, [qc]);

  const value = useMemo(() => ({ login, logout }), [login, logout]);

  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg text-[13px] text-muted">
        {bootError ? (
          <>
            <p className="font-medium text-ink">Không kết nối được máy chủ</p>
            <p>Kiểm tra kết nối mạng rồi thử lại.</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={boot}>
                Thử lại
              </Button>
              <Button size="sm" variant="ghost" onClick={() => session.clear()}>
                Đăng nhập lại
              </Button>
            </div>
          </>
        ) : (
          <>
            <Spinner className="size-5" />
            <p>Đang khôi phục phiên đăng nhập…</p>
          </>
        )}
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
